import type { ConfigurableVariantFragment } from '~~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';

export type VariantOptionSelection = Readonly<Record<string, string>>;

type VariantAttribute = {
  code?: string | null;
  uid?: string | null;
};

function isSelectionComplete(
  selection: VariantOptionSelection,
  optionCodes: ReadonlyArray<string>
): boolean {
  if (Object.keys(selection).length === 0) {
    return false;
  }

  if (optionCodes.length === 0) {
    return true;
  }

  return optionCodes.every((code) => code in selection);
}

function variantAttributesHaveCodes(
  variants: ReadonlyArray<ConfigurableVariantFragment | null | undefined>
): boolean {
  return variants
    .filter(isDefined)
    .some((variant) =>
      variant.attributes?.some((attribute) => Boolean(attribute?.code))
    );
}

function variantHasOptionAtCode(
  attributes: ReadonlyArray<VariantAttribute>,
  optionCode: string,
  optionUid: string,
  optionCodes: ReadonlyArray<string>,
  attributesHaveCodes: boolean
): boolean {
  if (attributesHaveCodes) {
    return attributes.some(
      (attribute) =>
        attribute.code === optionCode && attribute.uid === optionUid
    );
  }

  const optionIndex = optionCodes.indexOf(optionCode);
  return optionIndex >= 0 && attributes[optionIndex]?.uid === optionUid;
}

function variantMatchesSelectionEntry(
  attributes: ReadonlyArray<VariantAttribute>,
  code: string,
  uid: string,
  optionCodes: ReadonlyArray<string>,
  attributesHaveCodes: boolean
): boolean {
  if (attributesHaveCodes) {
    return attributes.some(
      (attribute) => attribute.code === code && attribute.uid === uid
    );
  }

  const optionIndex = optionCodes.indexOf(code);
  return optionIndex >= 0 && attributes[optionIndex]?.uid === uid;
}

/**
 * Returns the variant that matches selecting `optionUid` in `optionCode` while
 * keeping all other entries in `currentSelection` unchanged.
 */
export function findVariantForOptionSelection(
  optionCode: string,
  optionUid: string,
  currentSelection: VariantOptionSelection,
  variants:
    | ReadonlyArray<ConfigurableVariantFragment | null | undefined>
    | null
    | undefined,
  optionCodes: ReadonlyArray<string> = []
): ConfigurableVariantFragment | undefined {
  if (!optionCode || !optionUid || !variants) {
    return undefined;
  }

  if (!isSelectionComplete(currentSelection, optionCodes)) {
    return undefined;
  }

  const definedVariants = variants.filter(isDefined);
  const attributesHaveCodes = variantAttributesHaveCodes(definedVariants);
  const otherSelections = Object.entries(currentSelection).filter(
    ([code]) => code !== optionCode
  );

  return definedVariants.find((variant) => {
    const attributes = variant.attributes?.filter(isDefined) ?? [];
    const hasOption = variantHasOptionAtCode(
      attributes,
      optionCode,
      optionUid,
      optionCodes,
      attributesHaveCodes
    );

    if (!hasOption) {
      return false;
    }

    return otherSelections.every(([code, uid]) =>
      variantMatchesSelectionEntry(
        attributes,
        code,
        uid,
        optionCodes,
        attributesHaveCodes
      )
    );
  });
}

/**
 * Whether `optionUid` in `optionCode` forms a valid configuration together with
 * the other selected options.
 */
export function isVariantOptionSelectable(
  optionCode: string,
  optionUid: string,
  currentSelection: VariantOptionSelection,
  variants:
    | ReadonlyArray<ConfigurableVariantFragment | null | undefined>
    | null
    | undefined,
  optionCodes: ReadonlyArray<string> = [],
  requireProductUrlKey = false
): boolean {
  const variant = findVariantForOptionSelection(
    optionCode,
    optionUid,
    currentSelection,
    variants,
    optionCodes
  );

  if (!variant) {
    return false;
  }

  if (requireProductUrlKey && !variant.product?.url_key) {
    return false;
  }

  return true;
}

/**
 * Builds a code → uid map from the currently displayed variant attributes.
 */
export function buildVariantOptionSelectionFromAttributes(
  attributes:
    ReadonlyArray<VariantAttribute | null | undefined> | null | undefined,
  optionCodes: ReadonlyArray<string> = []
): Record<string, string> {
  const selection: Record<string, string> = {};

  for (const attribute of attributes ?? []) {
    if (attribute?.code && attribute?.uid) {
      selection[attribute.code] = attribute.uid;
    }
  }

  if (optionCodes.length === 0) {
    return selection;
  }

  if (optionCodes.every((code) => code in selection)) {
    return selection;
  }

  const uids = (attributes ?? [])
    .map((attribute) => attribute?.uid)
    .filter(isDefined);

  optionCodes.forEach((code, index) => {
    if (!(code in selection)) {
      const uid = uids[index];
      if (uid) {
        selection[code] = uid;
      }
    }
  });

  return selection;
}
